import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Building, Download, KeyRound, Plus, Upload, User, Users } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { CONTACT_KIND_LABELS, formatNumber, hasPermission, labelOf, PARTY_ROLE_FILTER_LABELS, PARTY_SOURCE_LABELS, PARTY_TYPE_LABELS } from '@blimo/shared';
import { Badge, Button, DataTable, ErrorState, PageHeader, SearchInput, StatCard } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { SourceBadge } from '../../components/data.jsx';
import { useListState } from '../../lib/list-search.js';

const toOptions = (labels) => Object.entries(labels).map(([value, label]) => ({ value, label }));

export function PartiesListPage() {
  const api = useApi();
  const { data: me } = useMe();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const filter = { q: search.q, type: search.type, role: search.role, source: search.source };
  const params = { ...list.apiParams, filter };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.parties(api, params));
  const columns = useMemo(() => partyColumns(), []);

  return (
    <>
      <PageHeader
        title="Personen & Organisationen"
        description="Eigentümer, Mieter, Mandanten, Verwalter, Kassenprüfer und Ableser."
        actions={
          <>
            {hasPermission(me, 'parties.export') && (
              <Button asChild size="sm" variant="outline">
                <a href={api.admin.parties.exportUrl(filter)} download>
                  <Download aria-hidden="true" /> Export (XLSX)
                </a>
              </Button>
            )}
            {hasPermission(me, 'parties.import') && (
              <Button asChild size="sm" variant="outline">
                <Link to="/personen/import">
                  <Upload aria-hidden="true" /> Importieren
                </Link>
              </Button>
            )}
            {hasPermission(me, 'parties.create') && (
              <Button asChild size="sm">
                <Link to="/personen/neu">
                  <Plus aria-hidden="true" /> Neu anlegen
                </Link>
              </Button>
            )}
          </>
        }
      />
      <PartyStatistics />
      <FilterBar>
        <SearchInput
          label="Personen durchsuchen"
          placeholder="Name, Organisation, E-Mail, Telefon …"
          value={search.q ?? ''}
          onValueChange={(v) => list.setFilter('q', v)}
        />
        <FilterSelect label="Art" value={search.type} onChange={(v) => list.setFilter('type', v)} options={toOptions(PARTY_TYPE_LABELS)} />
        <FilterSelect label="Rolle" value={search.role} onChange={(v) => list.setFilter('role', v)} options={toOptions(PARTY_ROLE_FILTER_LABELS)} />
        <FilterSelect label="Quelle" value={search.source} onChange={(v) => list.setFilter('source', v)} options={toOptions(PARTY_SOURCE_LABELS)} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Personen und Organisationen"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          sorting={list.sorting}
          onSortingChange={list.onSortingChange}
          emptyTitle="Keine Personen oder Organisationen gefunden"
        />
      )}
    </>
  );
}

/** F-103: Kennzahlen über alle Personen (unabhängig von den Filtern); Fehler blenden die Karten aus, die Liste bleibt. */
function PartyStatistics() {
  const api = useApi();
  const { data: stats, isPending, error } = useQuery(adminQueries.partyStatistics(api));
  if (error) return null;
  const fmt = (n) => formatNumber(n ?? 0);
  return (
    <section aria-labelledby="party-stats-heading" className="mb-3">
      <h2 id="party-stats-heading" className="sr-only">
        Kennzahlen
      </h2>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Gesamt"
          icon={Users}
          loading={isPending}
          value={stats && fmt(stats.total)}
          hint={stats && `${fmt(stats.active)} aktiv · ${fmt(stats.archived)} archiviert`}
        />
        <StatCard label="Personen" icon={User} loading={isPending} value={stats && fmt(stats.by_type?.person)} />
        <StatCard
          label="Organisationen"
          icon={Building}
          loading={isPending}
          value={stats && fmt(stats.by_type?.organization)}
          hint={stats && `${fmt(stats.by_role?.client)} Mandanten`}
        />
        <StatCard
          label="Mit Portalzugang"
          icon={KeyRound}
          loading={isPending}
          value={stats && fmt(stats.with_portal_account)}
          hint={stats && `${fmt(stats.by_role?.owner)} Eigentümer · ${fmt(stats.by_role?.tenant)} Mieter`}
        />
      </div>
    </section>
  );
}

/** Primary contact of a kind (falls back to the first one). */
function primary(contacts, kinds) {
  const matching = (contacts ?? []).filter((c) => kinds.includes(c.kind));
  return matching.find((c) => c.is_primary) ?? matching[0];
}

function partyColumns() {
  return [
    {
      id: 'last_name',
      accessorFn: (r) => r.display_name,
      header: 'Name',
      cell: ({ row }) => {
        const Icon = row.original.type === 'organization' ? Building : User;
        return (
          <span className="inline-flex items-center gap-1.5">
            <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-label={labelOf(PARTY_TYPE_LABELS, row.original.type)} />
            <Link to="/personen/$partyId" params={{ partyId: String(row.original.id) }} className="font-medium text-primary hover:underline">
              {row.original.display_name || `#${row.original.id}`}
            </Link>
            {row.original.archived_at && <Badge variant="neutral">Archiviert</Badge>}
          </span>
        );
      },
    },
    { accessorKey: 'organization_name', header: 'Organisation', cell: ({ row }) => row.original.organization_name ?? '–' },
    {
      id: 'email',
      header: labelOf(CONTACT_KIND_LABELS, 'email'),
      enableSorting: false,
      cell: ({ row }) => <span className="break-all">{primary(row.original.contacts, ['email'])?.value ?? '–'}</span>,
    },
    {
      id: 'phone',
      header: 'Telefon',
      enableSorting: false,
      cell: ({ row }) => <span className="numeric">{primary(row.original.contacts, ['phone', 'mobile'])?.value ?? '–'}</span>,
    },
    { id: 'source', header: 'Quelle', enableSorting: false, cell: ({ row }) => <SourceBadge source={row.original.source} /> },
  ];
}
